import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { useCookies } from 'react-cookie';
import { useGetUserID } from '../hooks/useGetUserID';
import '../App.css';

const INGREDIENT_CATEGORIES = [
    'dish', 'pizza', 'powder', 'soup', 'sauce', 
    'bake', 'salad', 'meat', 'veg', 'fish', 'none'
];

export default function IngredientCategories() {
    const [cookies] = useCookies(["access_token"]);
    const [ingredients, setIngredients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [filter, setFilter] = useState('none'); // Default to showing uncategorized
    const [search, setSearch] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedIngredients, setSelectedIngredients] = useState([]);
    const [aiCategorizing, setAiCategorizing] = useState(false);
    
    // Authentication and user ID
    const userID = useGetUserID();
    const navigate = useNavigate();

    // Fetch all ingredients on component mount (Higgo-only)
    useEffect(() => {
        const storedUsername = localStorage.getItem('username');
        const isPowerUser = localStorage.getItem('isPowerUser') === 'true';
        if (storedUsername !== 'Higgo' || !isPowerUser) {
            navigate('/');
            return;
        }
        fetchIngredients();
    }, [navigate]);
    
    // Fetch ingredients from the API
    const fetchIngredients = async () => {
        try {
            setLoading(true);
            setError('');
            
            const response = await axios.get(`${API_BASE_URL}/ingredients`, {
                headers: {
                    Authorization: `Bearer ${cookies.access_token}`
                }
            });
            
            setIngredients(response.data);
        } catch (err) {
            setError('Failed to fetch ingredients: ' + (err.response?.data?.message || err.message));
            console.error('Error fetching ingredients:', err);
        } finally {
            setLoading(false);
        }
    };
    
    // Toggle selection of an ingredient
    const toggleIngredientSelection = (id) => {
        setSelectedIngredients(prev => 
            prev.includes(id)
                ? prev.filter(i => i !== id)
                : [...prev, id]
        );
    };
    
    // Select all visible ingredients
    const selectAllVisible = () => {
        const visibleIds = getFilteredIngredients().map(ing => ing._id);
        setSelectedIngredients(visibleIds);
    };
    
    // Clear all selections
    const clearSelection = () => {
        setSelectedIngredients([]);
    };
    
    // Update category for selected ingredients
    const updateCategories = async () => {
        if (!selectedCategory || selectedIngredients.length === 0) {
            setError('Please select both a category and at least one ingredient');
            return;
        }
        
        try {
            setLoading(true);
            setError('');
            setSuccess('');
            
            const updates = selectedIngredients.map(id => ({
                id,
                category: selectedCategory
            }));
            
            const response = await axios.post(
                `${API_BASE_URL}/ingredients/bulk-update-categories`,
                { updates },
                {
                    headers: {
                        Authorization: `Bearer ${cookies.access_token}`
                    }
                }
            );
            
            // Update local state
            setIngredients(prev => 
                prev.map(ing => 
                    selectedIngredients.includes(ing._id)
                        ? { ...ing, category: selectedCategory }
                        : ing
                )
            );
            
            setSuccess(`Updated ${response.data.results.filter(r => r.success).length} ingredients`);
            setSelectedIngredients([]);
        } catch (err) {
            setError('Failed to update categories: ' + (err.response?.data?.message || err.message));
            console.error('Error updating categories:', err);
        } finally {
            setLoading(false);
        }
    };
    
    // Use AI to categorize ingredients
    const startAICategorization = async () => {
        try {
            setAiCategorizing(true);
            setError('');
            setSuccess('');
            
            const response = await axios.post(
                `${API_BASE_URL}/ingredients/ai-categorize`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${cookies.access_token}`
                    }
                }
            );
            
            // Refresh the ingredients list
            await fetchIngredients();
            
            setSuccess(response.data.message);
        } catch (err) {
            setError('AI categorization failed: ' + (err.response?.data?.message || err.message));
            console.error('Error in AI categorization:', err);
        } finally {
            setAiCategorizing(false);
        }
    };
    
    // Filter ingredients based on category and search term
    const getFilteredIngredients = () => {
        return ingredients.filter(ing => {
            const matchesCategory = filter === 'all' || ing.category === filter;
            const matchesSearch = search === '' || ing.name.toLowerCase().includes(search.toLowerCase());
            return matchesCategory && matchesSearch;
        });
    };
    
    // Render ingredient cards
    const renderIngredients = () => {
        const filteredIngredients = getFilteredIngredients();
        
        if (filteredIngredients.length === 0) {
            return (
                <div className="no-ingredients">
                    <p>No ingredients found. Try changing the filter or search term.</p>
                </div>
            );
        }
        
        return (
            <div className="ingredients-grid">
                {filteredIngredients.map(ing => (
                    <div 
                        key={ing._id} 
                        className={`ingredient-card ${selectedIngredients.includes(ing._id) ? 'selected' : ''}`}
                        onClick={() => toggleIngredientSelection(ing._id)}
                    >
                        <div className="ingredient-header">
                            <h3>{ing.name}</h3>
                            <div className="ingredient-category">
                                {ing.category || 'none'}
                            </div>
                        </div>
                        <div className="ingredient-details">
                            <p><strong>Brand:</strong> {ing.brand}</p>
                            <p><strong>Cost:</strong> R{ing.costPerUnit.toFixed(2)} per {ing.baseUnit}</p>
                            {ing.description && (
                                <p><strong>Description:</strong> {ing.description}</p>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    };
    
    return (
        <div className="ingredient-categories-page">
            <h1>Ingredient Categories</h1>
            
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}
            
            <div className="controls-container">
                <div className="filter-controls">
                    <div className="filter-group">
                        <label htmlFor="categoryFilter">Filter by Category:</label>
                        <select 
                            id="categoryFilter"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className="filter-select"
                        >
                            <option value="all">All Categories</option>
                            {INGREDIENT_CATEGORIES.map(cat => (
                                <option key={cat} value={cat}>
                                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                                </option>
                            ))}
                        </select>
                    </div>
                    
                    <div className="search-group">
                        <label htmlFor="searchInput">Search:</label>
                        <input
                            id="searchInput"
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search ingredients..."
                            className="search-input"
                        />
                    </div>
                </div>
                
                <div className="ai-controls">
                    <button 
                        onClick={startAICategorization}
                        disabled={aiCategorizing || loading}
                        className="ai-categorize-btn"
                    >
                        {aiCategorizing ? 'AI Categorizing...' : 'Auto-Categorize With AI'}
                    </button>
                </div>
                
                <div className="selection-controls">
                    <div className="selection-info">
                        <span>{selectedIngredients.length} ingredients selected</span>
                        <div className="selection-actions">
                            <button onClick={selectAllVisible} className="select-all-btn">Select All Visible</button>
                            <button onClick={clearSelection} className="clear-btn">Clear Selection</button>
                        </div>
                    </div>
                    
                    <div className="category-assignment">
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            disabled={selectedIngredients.length === 0}
                            className="category-select"
                        >
                            <option value="">Select a category...</option>
                            {INGREDIENT_CATEGORIES.map(cat => (
                                <option key={cat} value={cat}>
                                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                                </option>
                            ))}
                        </select>
                        
                        <button
                            onClick={updateCategories}
                            disabled={!selectedCategory || selectedIngredients.length === 0 || loading}
                            className="update-btn"
                        >
                            {loading ? 'Updating...' : 'Update Categories'}
                        </button>
                    </div>
                </div>
            </div>
            
            {loading && <div className="loading">Loading ingredients...</div>}
            
            {!loading && renderIngredients()}
            
            {/* Summary of categories */}
            {!loading && (
                <div className="category-summary">
                    <h3>Category Summary</h3>
                    <div className="category-stats">
                        {INGREDIENT_CATEGORIES.map(cat => {
                            const count = ingredients.filter(ing => ing.category === cat).length;
                            return (
                                <div key={cat} className="category-stat">
                                    <span className="category-name">
                                        {cat.charAt(0).toUpperCase() + cat.slice(1)}:
                                    </span>
                                    <span className="category-count">{count}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

// Add CSS for styling the ingredient categories page
const styles = `
.ingredient-categories-page {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
}

.controls-container {
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.filter-controls {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 16px;
}

.filter-group, .search-group {
    flex: 1;
    min-width: 200px;
}

.filter-select, .search-input, .category-select {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 14px;
}

.ai-controls {
    margin-bottom: 16px;
}

.ai-categorize-btn {
    background-color: #6f42c1;
    color: white;
    border: none;
    padding: 10px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
}

.ai-categorize-btn:hover {
    background-color: #5a32a3;
}

.ai-categorize-btn:disabled {
    background-color: #c8b8e6;
    cursor: not-allowed;
}

.selection-controls {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
}

.selection-info {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.selection-actions {
    display: flex;
    gap: 8px;
}

.select-all-btn, .clear-btn {
    background-color: transparent;
    border: 1px solid #ced4da;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    cursor: pointer;
}

.select-all-btn:hover, .clear-btn:hover {
    background-color: #f1f3f5;
}

.category-assignment {
    display: flex;
    gap: 12px;
    align-items: center;
}

.update-btn {
    background-color: #28a745;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
}

.update-btn:hover {
    background-color: #218838;
}

.update-btn:disabled {
    background-color: #86d49b;
    cursor: not-allowed;
}

.ingredients-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 20px;
    margin-top: 20px;
}

.ingredient-card {
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 16px;
    background-color: white;
    cursor: pointer;
    transition: all 0.2s ease;
}

.ingredient-card:hover {
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    transform: translateY(-2px);
}

.ingredient-card.selected {
    border-color: #007bff;
    background-color: #e6f2ff;
}

.ingredient-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
}

.ingredient-header h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #343a40;
}

.ingredient-category {
    padding: 2px 8px;
    background-color: #f8f9fa;
    border-radius: 30px;
    font-size: 12px;
    font-weight: 500;
    color: #495057;
    text-transform: capitalize;
}

.ingredient-details p {
    margin: 6px 0;
    font-size: 14px;
    color: #6c757d;
}

.error-message, .success-message {
    padding: 12px;
    border-radius: 4px;
    margin-bottom: 16px;
}

.error-message {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
}

.success-message {
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
}

.loading {
    text-align: center;
    padding: 20px;
    font-size: 16px;
    color: #6c757d;
}

.no-ingredients {
    text-align: center;
    padding: 40px;
    background-color: #f8f9fa;
    border-radius: 8px;
    color: #6c757d;
}

.category-summary {
    margin-top: 30px;
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 16px;
}

.category-summary h3 {
    margin-top: 0;
    margin-bottom: 12px;
    font-size: 18px;
    color: #343a40;
}

.category-stats {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 12px;
}

.category-stat {
    display: flex;
    justify-content: space-between;
    padding: 8px 12px;
    background-color: white;
    border-radius: 4px;
    border: 1px solid #dee2e6;
}

.category-name {
    font-weight: 500;
    color: #495057;
}

.category-count {
    font-weight: 600;
    color: #007bff;
}

@media (max-width: 768px) {
    .selection-controls {
        flex-direction: column;
        align-items: stretch;
    }
    
    .category-assignment {
        flex-direction: column;
    }
    
    .ingredients-grid {
        grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    }
}
`;

// Append styles to the document head
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = styles;
document.head.appendChild(styleSheet); 